/**
 * Light / dark theme override styles for Capacity Planner.
 *
 * Strategy: wrap the whole planner in a `.theme-light` div and use CSS to
 * remap the Tailwind arbitrary-value classes to light-mode equivalents.
 * This avoids rewriting every className while keeping the dark theme intact.
 */
export const CAPACITY_THEME_CSS = `
.theme-light [style*="linear-gradient(180deg,#0a1628"] {
  background: linear-gradient(180deg,#f8fafc 0%,#eef2f7 100%) !important;
  color: #0f172a !important;
}

/* Backgrounds */
.theme-light .bg-\\[\\#0a1628\\]\\/95 { background-color: rgb(255 255 255 / 0.9) !important; }
.theme-light .bg-\\[\\#0a1628\\] { background-color: #ffffff !important; }
.theme-light .bg-\\[\\#0f1a2e\\] { background-color: #ffffff !important; }
.theme-light .bg-slate-900\\/60 { background-color: #ffffff !important; }
.theme-light .bg-slate-900\\/40 { background-color: #f8fafc !important; }
.theme-light .bg-slate-800\\/80 { background-color: #f1f5f9 !important; }
.theme-light .bg-slate-800\\/60 { background-color: #f1f5f9 !important; }
.theme-light .bg-slate-800\\/40 { background-color: #e2e8f0 !important; }
.theme-light .bg-slate-800 { background-color: #f1f5f9 !important; }
.theme-light .bg-slate-700 { background-color: #e2e8f0 !important; }
.theme-light .bg-white\\/\\[0\\.02\\] { background-color: #f8fafc !important; }
.theme-light .bg-white\\/\\[0\\.01\\] { background-color: #fafafa !important; }
.theme-light .bg-white\\/\\[0\\.03\\] { background-color: #f1f5f9 !important; }
.theme-light .bg-white\\/5 { background-color: #f1f5f9 !important; }
.theme-light .bg-white\\/10 { background-color: #e2e8f0 !important; }
.theme-light .bg-white\\/20:hover { background-color: #cbd5e1 !important; }

/* Borders */
.theme-light .border-white\\/5 { border-color: #f1f5f9 !important; }
.theme-light .border-white\\/10 { border-color: #e2e8f0 !important; }
.theme-light .border-white\\/20 { border-color: #cbd5e1 !important; }

/* Text — invert the slate scale */
.theme-light .text-slate-100 { color: #0f172a !important; }
.theme-light .text-slate-200 { color: #1e293b !important; }
.theme-light .text-slate-300 { color: #334155 !important; }
.theme-light .text-slate-400 { color: #475569 !important; }
.theme-light .text-slate-500 { color: #64748b !important; }
.theme-light .text-slate-600 { color: #94a3b8 !important; }
.theme-light .placeholder-slate-500::placeholder { color: #94a3b8 !important; }

/* Hover states */
.theme-light .hover\\:bg-white\\/5:hover { background-color: #f1f5f9 !important; }
.theme-light .hover\\:bg-white\\/10:hover { background-color: #e2e8f0 !important; }
.theme-light .hover\\:bg-white\\/\\[0\\.03\\]:hover { background-color: #f8fafc !important; }

/* Cyan accents stay bright but darker text-on-cyan for contrast */
.theme-light .text-cyan-400 { color: #0891b2 !important; }
.theme-light .text-cyan-300 { color: #0e7490 !important; }
.theme-light .text-cyan-200 { color: #155e75 !important; }
.theme-light .text-emerald-300 { color: #059669 !important; }
.theme-light .text-emerald-200 { color: #047857 !important; }
.theme-light .text-amber-300 { color: #b45309 !important; }
.theme-light .text-amber-200 { color: #92400e !important; }
.theme-light .text-red-200 { color: #b91c1c !important; }
.theme-light .text-red-300 { color: #dc2626 !important; }
.theme-light .text-red-400 { color: #dc2626 !important; }
.theme-light .text-orange-300 { color: #c2410c !important; }
.theme-light .text-purple-400 { color: #7c3aed !important; }
.theme-light .text-yellow-400 { color: #b45309 !important; }
.theme-light .text-sky-300 { color: #0369a1 !important; }
.theme-light .text-sky-400 { color: #0284c7 !important; }

/* Shadows */
.theme-light .shadow-lg { box-shadow: 0 10px 15px -3px rgba(15,23,42,0.08), 0 4px 6px -4px rgba(15,23,42,0.06) !important; }
.theme-light .shadow-xl { box-shadow: 0 20px 25px -5px rgba(15,23,42,0.10), 0 8px 10px -6px rgba(15,23,42,0.06) !important; }

/* Modal backdrop softer in light mode */
.theme-light .bg-black\\/70 { background-color: rgba(15, 23, 42, 0.35) !important; }
/* Group header row */
.theme-light .bg-white\\/\\[0\\.02\\] { background-color: #f8fafc !important; }

/* Emerald-tinted buttons (Insert row) — keep readable in light mode */
.theme-light .bg-emerald-500\\/20 { background-color: #d1fae5 !important; }
.theme-light .bg-emerald-500\\/10 { background-color: #ecfdf5 !important; }
.theme-light .hover\\:bg-emerald-500\\/30:hover { background-color: #a7f3d0 !important; }
.theme-light .border-emerald-400\\/40 { border-color: #6ee7b7 !important; }
.theme-light .border-emerald-400\\/60 { border-color: #34d399 !important; }
.theme-light .border-emerald-500\\/30 { border-color: #6ee7b7 !important; }
.theme-light .text-emerald-100 { color: #065f46 !important; }

/* Cyan-tinted buttons (Insert row, Current, chips) */
.theme-light .bg-cyan-500\\/20 { background-color: #cffafe !important; }
.theme-light .bg-cyan-500\\/10 { background-color: #ecfeff !important; }
.theme-light .bg-cyan-500\\/5  { background-color: #f0f9ff !important; }
.theme-light .hover\\:bg-cyan-500\\/30:hover { background-color: #a5f3fc !important; }
.theme-light .border-cyan-400\\/40 { border-color: #67e8f9 !important; }
.theme-light .border-cyan-500\\/30 { border-color: #67e8f9 !important; }
.theme-light .border-cyan-400\\/30 { border-color: #67e8f9 !important; }
.theme-light .border-cyan-400\\/50 { border-color: #22d3ee !important; }

/* Amber tinted (override + carry-over + warnings) */
.theme-light .bg-amber-500\\/10 { background-color: #fffbeb !important; }
.theme-light .bg-amber-500\\/20 { background-color: #fef3c7 !important; }
.theme-light .bg-amber-500\\/15 { background-color: #fef3c7 !important; }
.theme-light .border-amber-400\\/30 { border-color: #fcd34d !important; }
.theme-light .border-amber-400\\/60 { border-color: #f59e0b !important; }
.theme-light .border-amber-500\\/30 { border-color: #fcd34d !important; }
.theme-light .hover\\:bg-amber-500\\/20:hover { background-color: #fef3c7 !important; }
.theme-light .hover\\:bg-amber-400:hover { background-color: #fbbf24 !important; }

/* Red / danger */
.theme-light .bg-red-500\\/5  { background-color: #fef2f2 !important; }
.theme-light .bg-red-500\\/10 { background-color: #fee2e2 !important; }
.theme-light .bg-red-500\\/15 { background-color: #fee2e2 !important; }
.theme-light .bg-red-500\\/20 { background-color: #fecaca !important; }
.theme-light .bg-red-600\\/25 { background-color: #fecaca !important; }
.theme-light .border-red-500\\/30 { border-color: #fca5a5 !important; }
.theme-light .border-red-500\\/40 { border-color: #f87171 !important; }
.theme-light .border-red-500\\/50 { border-color: #f87171 !important; }
.theme-light .border-l-red-500 { border-left-color: #dc2626 !important; }

/* Orange / sky / purple */
.theme-light .bg-orange-500\\/5  { background-color: #fff7ed !important; }
.theme-light .bg-sky-500\\/5     { background-color: #f0f9ff !important; }
.theme-light .bg-sky-500\\/15    { background-color: #e0f2fe !important; }
.theme-light .bg-purple-500\\/10 { background-color: #f5f3ff !important; }
.theme-light .bg-yellow-500\\/10 { background-color: #fefce8 !important; }
.theme-light .bg-yellow-500\\/15 { background-color: #fef9c3 !important; }
.theme-light .bg-orange-500\\/15 { background-color: #ffedd5 !important; }

/* Ring / focus rings for the light background */
.theme-light .ring-cyan-400\\/40 { --tw-ring-color: rgba(34,211,238,0.35) !important; }
.theme-light .ring-cyan-400\\/25 { --tw-ring-color: rgba(34,211,238,0.2) !important; }

/* react-day-picker calendar — force readable colours in light mode */
.theme-light .rdp,
.theme-light .rdp-months,
.theme-light .rdp-month,
.theme-light .rdp-table,
.theme-light .rdp-day,
.theme-light .rdp-head_cell,
.theme-light .rdp-caption_label,
.theme-light .rdp-weeknumber {
  color: #0f172a !important;
}
.theme-light .rdp-day:hover:not(.rdp-day_selected) {
  background-color: #e0f2fe !important;
  color: #0369a1 !important;
}
.theme-light .rdp-day_selected {
  background: #06b6d4 !important;
  color: #ffffff !important;
}
.theme-light .rdp-day_today:not(.rdp-day_selected) {
  border: 1px solid #06b6d4 !important;
  font-weight: 900 !important;
}
.theme-light .rdp-nav_button svg {
  color: #0369a1 !important;
}
.theme-light .rdp-head_cell {
  color: #475569 !important;
}
.theme-light .rdp-weeknumber {
  color: #0891b2 !important;
  font-weight: 900 !important;
}
`;

export function getThemePreference() {
  const KEY = "capacity_theme";
  const initial = (() => {
    try {
      return window.localStorage.getItem(KEY) || "light";
    } catch { return "light"; }
  })();

  return {
    initial,
    persist(next) {
      try { window.localStorage.setItem(KEY, next); } catch { /* ignore */ }
    },
  };
}
